import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { ArtReproduction, Tag, Size, SizePrice, ArtistOption } from '../types';
import { trackOptionEvent } from '../services/metrics';
import ArtworkCard from '../components/gallery/ArtworkCard';
import ArtworkModal from '../components/gallery/ArtworkModal';
import GalleryFilters from '../components/gallery/GalleryFilters';
import { useToast } from '../contexts/ToastContext';
import './ArtGallery.css';

interface ExtendedArtReproduction extends ArtReproduction {
  sizePrices?: SizePrice[];
  tags?: Tag[];
}

interface SelectedSize {
  size: string;
  price: number;
  isOriginal?: boolean;
}

const ArtGallery: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const [artworks, setArtworks] = useState<ExtendedArtReproduction[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [availableSizes, setAvailableSizes] = useState<Size[]>([]);
  const [artists, setArtists] = useState<ArtistOption[]>([]);
  const [selectedArtist, setSelectedArtist] = useState<string>('');
  const [selectedArtistTier, setSelectedArtistTier] = useState<string>('');
  const [selectedSizes, setSelectedSizes] = useState<Record<number, SelectedSize>>({});
  const [selectedArtwork, setSelectedArtwork] = useState<ExtendedArtReproduction | null>(null);
  const [showArtworkModal, setShowArtworkModal] = useState<boolean>(false);
  const [addingToCart, setAddingToCart] = useState<boolean>(false);
  const { success, error: showError, warning } = useToast();

  useEffect(() => {
    fetchTags();
    fetchSizes();
    fetchArtists();
  }, []);

  const fetchSizes = async (): Promise<void> => {
    try {
      const response = await api.get<Size[]>('/sizes');
      setAvailableSizes(response.data);
    } catch (error) {
      console.error('Failed to fetch sizes:', error);
    }
  };

  const fetchTags = async (): Promise<void> => {
    try {
      const response = await api.get<Tag[]>('/tags');
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchArtists = async (): Promise<void> => {
    try {
      const response = await api.get<ArtistOption[]>('/art/artists');
      setArtists(response.data);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    }
  };

  const sortArtworksByDemand = useCallback(
    (items: ExtendedArtReproduction[]): ExtendedArtReproduction[] => {
      // Simple alphabetical sort for now - can be enhanced with demand data later
      return [...items].sort((a, b) => a.title.localeCompare(b.title));
    },
    []
  );

  const fetchArtworks = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (searchQuery) params.search = searchQuery;
      if (selectedTags.length > 0) params.tagIds = selectedTags.join(',');
      if (selectedSize) params.size = selectedSize;
      if (selectedArtist) {
        if (selectedArtist.startsWith('id:')) {
          params.artistId = selectedArtist.replace('id:', '');
        } else if (selectedArtist.startsWith('name:')) {
          params.artistName = selectedArtist.replace('name:', '');
        }
      }
      if (selectedArtistTier) {
        params.artistTier = selectedArtistTier;
      }

      // Check URL params for initial filters
      const urlArtist = searchParams.get('artist');
      const urlArtwork = searchParams.get('artwork');
      const urlTag = searchParams.get('tag');

      if (urlArtist && !selectedArtist) {
        const decoded = decodeURIComponent(urlArtist);
        setSelectedArtist(decoded);
        if (decoded.startsWith('id:')) {
          params.artistId = decoded.replace('id:', '');
        } else if (decoded.startsWith('name:')) {
          params.artistName = decoded.replace('name:', '');
        }
      }

      if (urlArtwork) {
        params.artworkId = urlArtwork;
      }

      if (urlTag) {
        const tagId = parseInt(urlTag);
        if (!selectedTags.includes(tagId)) {
          setSelectedTags([tagId]);
          params.tagIds = urlTag;
        }
      }

      const response = await api.get<ExtendedArtReproduction[]>('/art', { params });
      setArtworks(sortArtworksByDemand(response.data));
    } catch (error) {
      console.error('Failed to fetch artworks:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedTags, selectedSize, selectedArtist, selectedArtistTier, sortArtworksByDemand, searchParams]);

  useEffect(() => {
    fetchArtworks();
  }, [fetchArtworks]);


  const sortedArtists = useMemo(() => {
    return [...artists].sort((a, b) => {
      const aName = a.artistName || 'Unknown Artist';
      const bName = b.artistName || 'Unknown Artist';
      return aName.localeCompare(bName);
    });
  }, [artists]);



  const handleTagToggle = (tagId: number): void => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleArtistChange = (value: string): void => {
    if (!value) {
      setSelectedArtist('');
      return;
    }

    // If artist has an ID, navigate to their storefront
    if (value.startsWith('id:')) {
      const artistId = value.replace('id:', '');
      navigate(`/artists/${artistId}/shop`);
    } else {
      // Otherwise, just filter by name
      setSelectedArtist(value);
    }
  };

  const handleSizeSelect = (artworkId: number, sizePrice: SelectedSize): void => {
    setSelectedSizes(prev => ({
      ...prev,
      [artworkId]: sizePrice
    }));
  };

  const handleAddToCart = async (artwork: ExtendedArtReproduction, options?: { material?: string; frame?: string; hanging?: string; price?: number }): Promise<void> => {
    try {
      if (addingToCart) return;
      const selectedSize = selectedSizes[artwork.id];
      if (!selectedSize && artwork.sizePrices && artwork.sizePrices.length > 0) {
        warning('Please select a size.');
        return;
      }

      const price = options?.price ?? selectedSize?.price ?? artwork.price;
      const size = selectedSize?.size || 'Standard';
      const isOriginal = selectedSize?.isOriginal || false;
      
      // Check if original is still available
      if (isOriginal && !artwork.isOriginalAvailable) {
        showError('This original artwork is no longer available.');
        return;
      }
      
      setAddingToCart(true);
      // Create product from artwork
      const productData: any = {
        name: `${artwork.title}${size !== 'Standard' ? ` - ${size}` : ''}`,
        description: artwork.description || `Art by ${artwork.artistName || 'Unknown Artist'}`,
        price: price,
        imageUrl: artwork.imageUrl,
        size: size,
        material: options?.material,
        finish: options?.frame || options?.hanging ? `${options.frame || ''}${options.frame && options.hanging ? ', ' : ''}${options.hanging || ''}`.trim() : undefined,
        isCustomPrint: false,
        artReproductionId: artwork.id,
        isOriginal: isOriginal,
      };

      const productResponse = await api.post<{ id: number }>('/products', productData);
      await api.post('/cart', { productId: productResponse.data.id, quantity: 1 });
      await trackOptionEvent({
        itemType: 'Art',
        itemId: artwork.id,
        size: size,
        eventType: 'Carted'
      });
      success('Added to cart!');
    } catch (error) {
      console.error('Failed to add to cart:', error);
      showError('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleOpenArtwork = async (artwork: ExtendedArtReproduction): Promise<void> => {
    setSelectedArtwork(artwork);
    setShowArtworkModal(true);
    try {
      await trackOptionEvent({
        itemType: 'Art',
        itemId: artwork.id,
        eventType: 'Viewed'
      });
    } catch {
      // ignore
    }
  };


  const handleClearFilters = (): void => {
    setSelectedTags([]);
    setSelectedSize('');
    setSelectedArtist('');
    setSelectedArtistTier('');
    navigate('/art/browse');
  };

  const activeFiltersCount = selectedTags.length + (selectedSize ? 1 : 0) + (selectedArtist ? 1 : 0) + (selectedArtistTier ? 1 : 0);

  return (
    <div className="art-gallery-page">
      <div className="container">
        <div className="gallery-header">
          <div className="gallery-header-top">
            <h1 className="page-title">Browse Artwork</h1>
            <button
              type="button"
              onClick={() => navigate('/art')}
              className="back-to-shop-button"
            >
              <i className="fas fa-arrow-left"></i> Back to Shop
            </button>
          </div>
          {activeFiltersCount > 0 && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="clear-filters-button"
            >
              Clear All Filters ({activeFiltersCount})
            </button>
          )}
        </div>
        
        {searchQuery && (
          <div className="search-query-banner">
            <span>Search results for: <strong>"{searchQuery}"</strong></span>
            <button
              type="button"
              onClick={() => navigate('/art/browse')}
              className="clear-search-button"
              aria-label="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        <div className="gallery-layout">
          <GalleryFilters
            tags={tags}
            availableSizes={availableSizes}
            selectedTags={selectedTags}
            selectedSize={selectedSize}
            artists={sortedArtists}
            selectedArtist={selectedArtist}
            selectedArtistTier={selectedArtistTier}
            onTagToggle={handleTagToggle}
            onSizeChange={setSelectedSize}
            onArtistChange={handleArtistChange}
            onArtistTierChange={setSelectedArtistTier}
          />
          <section className="gallery-results">
            {loading ? (
              <div className="loading">Loading artworks...</div>
            ) : artworks.length === 0 ? (
              <div className="no-results">
                <p>No artworks found. Try adjusting your search or filters.</p>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="btn btn-primary"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                <div className="results-header">
                  <p className="results-count">{artworks.length} {artworks.length === 1 ? 'artwork' : 'artworks'} found</p>
                </div>
                <div className="artworks-grid">
                  {artworks.map((artwork) => (
                    <ArtworkCard 
                      key={artwork.id} 
                      artwork={artwork}
                      onOpen={handleOpenArtwork}
                    />
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
      <ArtworkModal
        isOpen={showArtworkModal}
        artwork={selectedArtwork}
        selectedSize={selectedArtwork ? selectedSizes[selectedArtwork.id] : undefined}
        onSizeSelect={handleSizeSelect}
        onAddToCart={handleAddToCart}
        isSubmitting={addingToCart}
        onClose={() => {
          setShowArtworkModal(false);
          setSelectedArtwork(null);
        }}
      />
    </div>
  );
};

export default ArtGallery;
